/**
 * @file   src\components\FilterDropdown.tsx
 * @brief  Dropdown component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { useEffect, useState, Dropdown, Button } from '../utils/thirdpartyComponents';
import FilterIcon from '../assets/images/icons/filter';
import CWSelect from './CWSelect';
import CWRadio from './CWRadio';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../components/Dropdown.scss";
import { ChangeEvent} from 'react';
import CWMultiSelect from './CWMultiSelect';
import SmallLoader from '../components/SmallLoader';
import { DashBoardApi } from "../utils/apiUrls";
import axios from '../config/axiosConfig';
import {  CompanyOption, Option, LocationOption } from "../interfaces/CarWashInterface";
import { DashboardFilter } from '../interfaces/generalInterface';
import { userType } from '../utils/enums';
import moment from "moment-timezone"
const FilterDropdown: React.FC<DashboardFilter> = ({ filterDashBoard ,userRole}) => {
    const [CompanyOptions, setCompanyOptions] = useState<Option[]>([]);
    const [locations, setLocations] = useState<Option[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);

    const [selectedCompany, setSelectedCompany] = useState<{ label: string; value: string }[] | null>(CompanyOptions)
    const [selectedLocation, setSelectedLocation] = useState<{ label: string; value: string }[] | null>(locations)
    const [loading, setLoading] = useState(false);
    // State to keep track of the selected radio button
    const [selectedOption, setSelectedOption] = useState('month');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([])
    const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([])
    const [filterClass, setFilterClass] = useState('');

    // Handle changes for CWSelect
    const handleSelectChange = (selected: { label: string; value: string }[] | null, name: string) => {
        if (name === 'companyname') {
           
            setSelectedCompany(selected);
            const selectedIds: number[] = [];
            for(let i=0; i < (selected || []).length; i++) {
                const selected_value = (selected && selected[i] && selected[i]?.value) ? selected[i]?.value : 0
                selectedIds.push(Number(selected_value));
            }
            setSelectedCompanyIds(selectedIds)
        } else if (name === 'location') {
            setSelectedLocation(selected);
            const selectedIds = [];
            for(let i=0; i < (selected || []).length; i++) {
                const selected_value = (selected && selected[i] && selected[i]?.value) ? selected[i]?.value : 0
                selectedIds.push(Number(selected_value));
            }
            setSelectedLocationIds(selectedIds)
        }
    };

    // Handle radio button change
    const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSelectedOption(event.target.id);
    };

    // Handle DatePicker changes
    const handleDateChange = (setter: React.Dispatch<React.SetStateAction<Date | null>>) => (date: Date | null) => {
        setter(date);
    };

    // Handle form submission
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault(); // Prevent the default form submission behavior
        console.log(startDate)
        const locale = 'en-us';
        let month = null;
        let year = null
        const today = new Date();
        if(selectedOption === 'month') {
            month = today.getMonth()+1
        } else {
            year = today.getFullYear()
        }
        let filterChanged = false;
        let dateFilterChanged = false;
        let companyFilterChanged = false;
        let locationFilterChanged = false;
        let companyChanged = false;
        if(CompanyOptions.length !== selectedCompanyIds.length && selectedCompanyIds.length > 0){
            filterChanged = true;
            companyFilterChanged = true
        }
        if(selectedCompanyIds.length > 0) {
            companyChanged = true;
        }
        if(selectedOption !== 'month') {
            filterChanged = true;
            dateFilterChanged = true;
        }
        if(companyChanged === true && locations.length !== selectedLocationIds.length && selectedLocationIds.length > 0) {
            filterChanged = true;
            locationFilterChanged = true
        }
        if(filterChanged === true){
            setFilterClass('filtered')
        } else {
            setFilterClass('')
        }
        let fromDate = null;
        if(startDate) {
            
           // fromDate =  moment.utc(new Date(startDate)).format('YYYY-MM-DD')
           fromDate = new Date(startDate).toLocaleDateString(locale)
        }
        let toDate = null;
        if(endDate) {
           // toDate =  moment.utc(new Date(endDate)).format('YYYY-MM-DD')
           toDate = new Date(endDate).toLocaleDateString(locale)
        }
        // Get form values
        const formValues = {
            company: selectedCompanyIds ? selectedCompanyIds : null,
            location: selectedLocationIds ? selectedLocationIds : null,
            month: month,
            year: year,
            fromDate: fromDate,
            toDate: toDate,
            dateFilterType : selectedOption,
            filterChanged : filterChanged,
            defaultFilter : (!filterChanged) ? true : false,
            dateFilterChanged : dateFilterChanged,
            companyFilterChanged : companyFilterChanged,
            locationFilterChanged : locationFilterChanged
        };
        setDropdown()
        filterDashBoard(formValues)
    };

    //To clear form
    const handleClear = () => {
        setSelectedCompany([]);
        setSelectedLocation([]);
        setSelectedOption('month');
        setStartDate(null);
        setEndDate(null);
        const today = new Date();
        const formValues = {
            company: null,
            location: null,
            month: today.getMonth()+1,
            year: today.getFullYear(),
            fromDate: null,
            toDate: null,
            dateFilterType : 'month',
            defaultFilter : true,
            dateFilterChanged : false,
            companyFilterChanged : false,
            locationFilterChanged : false
        };
        filterDashBoard(formValues)
        setDropdown();
        setFilterClass('')
        setSelectedCompanyIds([]);
        setSelectedLocationIds([]);
        if(userRole == userType['SUPER-ADMIN']) {
        getFilterCompany();
        }
    }

    useEffect(() => {
        if(userRole == userType['SUPER-ADMIN']) {
            getFilterCompany()
        } else {
            getLocationsByCompany([])
        }
    }, [])

    useEffect(() => {
        if(selectedCompanyIds.length) {
            getLocationsByCompany(selectedCompanyIds)
        }
    }, [selectedCompanyIds])

    const getFilterCompany = async () => {
        try {
            setLoading(true)
            const { data } = await axios.get(DashBoardApi.DASHBOARD_FILTER_COMPANY_LIST);
            if(data?.error === 0) {
                setLoading(false)
                const company_list: CompanyOption[] = data?.data
                // Transform the array and add the first option
                if(company_list) {
                    const transformedArray: Option[]  = [
                        ...company_list.map(item => ({
                            value: item?.CarWashID,
                            label: item?.companyName
                        }))
                    ];
                   
                    setCompanyOptions(transformedArray)
                    setSelectedCompany(transformedArray);
                    setSelectedCompanyIds([])
                    const selectedIds: number[] = [];
                    for(let i=0; i < (transformedArray || []).length; i++) {
                        const selected_value = (transformedArray && transformedArray[i] && transformedArray[i]?.value) ? transformedArray[i]?.value : 0
                        selectedIds.push(Number(selected_value));
                    }
                    setSelectedCompanyIds(selectedIds)
                   
                } else {
                        setLoading(false)
                        setCompanyOptions([])
                    }

            } else {
                setLoading(false)
                setCompanyOptions([])
            }
        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };

    const getLocationsByCompany = async (companyIds : number[]) => {
        try {
            setLoading(true)
            const payLoad = {
                companyIds : companyIds
            }
            setSelectedLocation([])
            const { data } = (userRole == userType['SUPER-ADMIN']) ? await axios.post(DashBoardApi.DASHBOARD_FILTER_LOCATION_LIST, payLoad) :  await axios.get(DashBoardApi.CARWASH_OWNER_DASHBOARD_FILTER_LOCATION_LIST);
            if(data?.error === 0) {
                setLoading(false)
                const location_list: LocationOption[] = data?.data
                // Transform the array and add the first option
                if(location_list) {
                    const transformedArray: Option[]  = [
                        ...location_list.map(item => ({
                            value: item?.locationId,
                            label: item?.locationName
                        }))
                    ];
                    setLocations(transformedArray);

                    if((CompanyOptions.length === selectedCompanyIds.length || selectedCompanyIds.length === 0) && userRole === userType['SUPER-ADMIN']){
                        setSelectedLocation(transformedArray);
                    } else if(userRole === userType['CARWASH-OWNER']) {
                        setSelectedLocation(transformedArray);
                    }
                }

            } else {
                setLoading(false)
                setLocations([])
            }
        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };

    const setDropdown = () => {
        setShowDropdown(!showDropdown)
    }
    return (
        <Dropdown className={"filter-dropdown " + filterClass} align="end" show={showDropdown} onToggle={setDropdown}>
            <Dropdown.Toggle id="dropdown-autoclose-true" onClick={setDropdown}>
                Filters
                <FilterIcon />
            </Dropdown.Toggle>

            <Dropdown.Menu>
                <h6>Filter by</h6>
                <form onSubmit={handleSubmit}>
                    {/* <CWSelect
                        id="companyname"
                        name="companyname"
                        options={options}
                        label="Car Wash Company"
                        dropdownGap={0}
                        dropdownPosition="auto"
                        placeholder="Select Company"
                        onChange={handleSelectChange('companyname')}
                        values={selectedCompany}
                        multi={true}
                    /> */}
                    {userRole == userType['SUPER-ADMIN'] ? 
                    <CWMultiSelect
                        id="companyname"
                        name="companyname"
                        options={CompanyOptions}
                        label="Car Wash Company"
                        dropdownGap={0}
                        dropdownPosition="auto"
                        isMandatory
                        placeholder="Select Company"
                        onChange={(selectedOptions) => handleSelectChange(selectedOptions, "companyname")}
                        hasSelectAll={true}
                        value={selectedCompany}
                        labelAllSelected="All Companies Selected"
                    /> :"" }
                    <CWMultiSelect
                        id="location"
                        name="location"
                        options={locations}
                        label="Car Wash Location"
                        dropdownGap={0}
                        dropdownPosition="auto"
                        onChange={(selectedLocations) => handleSelectChange(selectedLocations, 'location')}
                        hasSelectAll={true}
                        labelAllSelected="All Locations Selected"
                        value={selectedLocation}
                    />
                    <div className="radio-field-group d-flex flex-column">
                        <CWRadio
                            id="month"
                            name="timeframe"
                            label="Current Month"
                            value="month"
                            checked={selectedOption === 'month'}
                            onChange={handleRadioChange}
                        />
                        <CWRadio
                            id="year"
                            name="timeframe"
                            label="Current Year"
                            value="year"
                            checked={selectedOption === 'year'}
                            onChange={handleRadioChange}
                        />
                        <CWRadio
                            id="custom"
                            name="timeframe"
                            label="Custom"
                            value="custom"
                            checked={selectedOption === 'custom'}
                            onChange={handleRadioChange}
                        />
                    </div>
                    {selectedOption === "custom" ? (<div className="calendar-field d-flex flex-column">
                        <DatePicker
                            showIcon
                            selected={startDate}
                            onChange={handleDateChange(setStartDate)}
                            placeholderText='From'
                        />
                        <DatePicker
                            showIcon
                            selected={endDate}
                            onChange={handleDateChange(setEndDate)}
                            placeholderText='To'
                        />
                    </div>) : null}

                    <div className="button-block d-flex justify-content-between">
                        <Button title="Clear" variant="light" className="light-btn" onClick={handleClear}>Reset</Button>
                        <Button title="Apply" variant="primary" className="primary-btn" type="submit">Apply</Button>
                    </div>
                </form>
                {loading && <SmallLoader />}
            </Dropdown.Menu>
        </Dropdown>
    );
};
export default FilterDropdown;
